import React, { useState } from 'react';
import {
    Box,
    Card,
    CircularProgress,
    Snackbar,
    Alert,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MovieIcon from '@mui/icons-material/Movie';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StarIcon from '@mui/icons-material/Star';
import { uploadLearningMedia, getLearningMedia } from '../../../../services/aprender';
import { t } from 'i18next';

const MediaGallery = ({
    availableMedia = [],
    uploadedMedia = null,
    setUploadedMedia,
    selectedExistingMedia = [],
    setSelectedExistingMedia,
    setContent
}) => {
    const [uploading, setUploading] = useState(false);
    const [mediaList, setMediaList] = useState(availableMedia);
    const [errorMessage, setErrorMessage] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewMedia, setPreviewMedia] = useState(null);

    const SUPPORTED_FILE_TYPES = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'video/mp4',
        'video/webm',
        'application/pdf',
        'audio/mpeg',
        'audio/wav'
    ];

    const handlePreview = (media) => {
        setPreviewMedia(media);
        setPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewMedia(null);
        setPreviewOpen(false);
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
            setErrorMessage(t('learning.media.unsupported_format'));
            return;
        }

        setUploading(true);
        try {
            const newMedia = await uploadLearningMedia(file);
            setUploadedMedia(newMedia);
            const updatedMedia = await getLearningMedia();
            setMediaList(updatedMedia);
        } catch (err) {
            console.error('Erro ao enviar media:', err);
            setErrorMessage(t('learning.media.upload_error'));
        } finally {
            setUploading(false);
        }
    };

    const handleSingleClick = (media) => {
        const ext = media.url.split('.').pop()?.toLowerCase();

        if (!['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
            return;
        }

        if (uploadedMedia && uploadedMedia.uuid === media.uuid) {
            setUploadedMedia(null);
        } else {
            setUploadedMedia(media);
        }
    };

    return (
        <>
            <Box display="flex" flexWrap="wrap" gap={2}>
                <Card
                    sx={{
                        width: 100,
                        height: 100,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                        border: '2px dashed #1976d2',
                        '&:hover': { backgroundColor: '#f0f7ff' }
                    }}
                    component="label"
                >
                    {uploading ? (
                        <CircularProgress />
                    ) : (
                        <AddIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                    )}
                    <input type="file" hidden onChange={handleUpload} />
                </Card>

                {mediaList.map((media) => {
                    const isFeatured = uploadedMedia?.uuid === media.uuid;
                    const ext = media.url.split('.').pop()?.toLowerCase();

                    let contentDisplay;
                    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
                        contentDisplay = (
                            <img
                                src={media.url}
                                alt=""
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '4px'
                                }}
                            />
                        );
                    } else if (['mp4', 'webm'].includes(ext)) {
                        contentDisplay = (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <MovieIcon sx={{ fontSize: 40, color: '#999' }} />
                            </Box>
                        );
                    } else if (ext === 'pdf') {
                        contentDisplay = (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <PictureAsPdfIcon sx={{ fontSize: 40, color: '#999' }} />
                            </Box>
                        );
                    } else if (['mp3', 'wav'].includes(ext)) {
                        contentDisplay = (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <AudiotrackIcon sx={{ fontSize: 40, color: '#999' }} />
                            </Box>
                        );
                    } else {
                        contentDisplay = (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 14,
                                    color: '#999',
                                    textAlign: 'center',
                                    p: 1
                                }}
                            >
                                {ext?.toUpperCase() || 'FILE'}
                            </Box>
                        );
                    }

                    const handleAddToEditor = (media) => {
                        const ext = media.url.split('.').pop()?.toLowerCase();
                        const quill = document.querySelector('.ql-container')?.__quill;
                        if (!quill) return;

                        const range = quill.getSelection(true);

                        if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
                            quill.insertEmbed(range.index, 'image', media.url);
                            quill.setSelection(range.index + 1);
                        } else if (['mp4', 'webm'].includes(ext)) {
                            quill.insertEmbed(range.index, 'video', media.url);
                            quill.setSelection(range.index + 1);
                        } else if (ext === 'pdf') {
                            const pdfLinkHtml = `
                                <p>
                                    Abrir documento PDF: 
                                    <a href="${media.url}" target="_blank" rel="noopener noreferrer">
                                        Ver PDF
                                    </a>
                                </p>
                            `;
                            quill.clipboard.dangerouslyPasteHTML(
                                range.index,
                                pdfLinkHtml
                            );
                            quill.setSelection(range.index + 1);
                        } else if (['mp3', 'wav'].includes(ext)) {
                            const audioHtml = `<audio controls style="width:100%;"><source src="${media.url}" type="audio/${ext}" /></audio>`;
                            quill.clipboard.dangerouslyPasteHTML(range.index, audioHtml);
                        }
                    };

                    return (
                        <Card
                            key={media.uuid}
                            onClick={() => handleSingleClick(media)}
                            sx={{
                                width: 100,
                                height: 100,
                                position: 'relative',
                                border: isFeatured
                                    ? '3px solid #ffb400'
                                    : '1px solid #ccc',
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            {contentDisplay}

                            {/* Ver */}
                            <IconButton
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    top: 2,
                                    right: 2,
                                    bgcolor: 'rgba(255,255,255,0.7)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePreview(media);
                                }}
                            >
                                <VisibilityIcon fontSize="small" />
                            </IconButton>

                            {/* Featured Media */}
                            {isFeatured && (
                                <StarIcon
                                    sx={{
                                        position: 'absolute',
                                        top: 2,
                                        left: 2,
                                        color: '#ffb400',
                                        bgcolor: 'rgba(255,255,255,0.7)',
                                        borderRadius: '50%',
                                        fontSize: 18
                                    }}
                                />
                            )}

                            {/* Add */}
                            <IconButton
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    bottom: 2,
                                    right: 2,
                                    bgcolor: 'rgba(25,118,210,0.8)',
                                    color: '#fff',
                                    '&:hover': { bgcolor: 'rgba(25,118,210,1)' }
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddToEditor(media);
                                }}
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </Card>
                    );
                })}
            </Box>

            {/* Preview */}
            <Dialog
                open={previewOpen}
                onClose={handleClosePreview}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{t('learning.media.preview_title')}</DialogTitle>
                <DialogContent sx={{ textAlign: 'center' }}>
                    {previewMedia &&
                    ['jpg', 'jpeg', 'png', 'gif'].includes(
                        previewMedia.url.split('.').pop()?.toLowerCase()
                    ) ? (
                        <img
                            src={previewMedia.url}
                            alt=""
                            style={{ maxWidth: '100%', maxHeight: '70vh' }}
                        />
                    ) : previewMedia &&
                      ['mp4', 'webm'].includes(
                          previewMedia.url.split('.').pop()?.toLowerCase()
                      ) ? (
                        <video controls style={{ maxWidth: '100%', maxHeight: '70vh' }}>
                            <source
                                src={previewMedia.url}
                                type={`video/${previewMedia.url.split('.').pop()?.toLowerCase()}`}
                            />
                        </video>
                    ) : previewMedia &&
                      previewMedia.url.split('.').pop()?.toLowerCase() === 'pdf' ? (
                        <iframe
                            src={previewMedia.url}
                            style={{ width: '100%', height: '70vh' }}
                            title="PDF Preview"
                        />
                    ) : previewMedia &&
                      ['mp3', 'wav'].includes(
                          previewMedia.url.split('.').pop()?.toLowerCase()
                      ) ? (
                        <audio controls>
                            <source
                                src={previewMedia.url}
                                type={`audio/${previewMedia.url.split('.').pop()?.toLowerCase()}`}
                            />
                        </audio>
                    ) : (
                        <p>{t('learning.media.preview_unavailable')}</p>
                    )}
                </DialogContent>
            </Dialog>

            <Snackbar
                open={!!errorMessage}
                autoHideDuration={4000}
                onClose={() => setErrorMessage('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setErrorMessage('')}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default MediaGallery;
